import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import {
  Users, Crown, Ban, Search, ChevronLeft, ChevronRight, Edit, Trash2, Eye,
  DollarSign, UserPlus, Loader2, AlertTriangle, RotateCcw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (dateString) => {
  if (!dateString) return 'Never';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

export const AdminUsersTab = ({ token, onStatsRefresh }) => {
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ proOnly: false, bannedOnly: false, humansOnly: true, botsOnly: false });
  const [selectedUser, setSelectedUser] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [userDetails, setUserDetails] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: pagination.page, limit: 20,
        ...(search && { search }),
        ...(filters.proOnly && { pro_only: true }),
        ...(filters.bannedOnly && { banned_only: true }),
        ...(filters.humansOnly && { humans_only: true }),
        ...(filters.botsOnly && { bots_only: true })
      });
      const res = await fetch(`${API_URL}/api/admin/users?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users);
        setPagination(prev => ({ ...prev, pages: data.pages, total: data.total }));
      }
    } catch (err) { console.error('Error fetching users:', err); }
  }, [token, pagination.page, search, filters]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const fetchUserDetails = async (userId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setUserDetails(await res.json());
    } catch (err) { console.error('Error fetching user details:', err); }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      if (res.ok) { toast.success('User updated'); fetchUsers(); onStatsRefresh?.(); setEditModalOpen(false); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed to update user'); }
    } catch { toast.error('Error updating user'); }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) { toast.success('User deleted'); fetchUsers(); onStatsRefresh?.(); setDeleteModalOpen(false); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed to delete user'); }
    } catch { toast.error('Error deleting user'); }
  };

  const handleCreateUser = async (userData) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users/create`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(userData)
      });
      if (res.ok) { toast.success('User created'); fetchUsers(); onStatsRefresh?.(); setCreateModalOpen(false); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed to create user'); }
    } catch { toast.error('Error creating user'); }
  };

  const handleSearch = (e) => { e.preventDefault(); setPagination(prev => ({ ...prev, page: 1 })); };

  return (
    <>
      <Card className="bg-slate-900/50 border-slate-800" data-testid="admin-users-tab">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <CardTitle className="text-white">Users</CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <form onSubmit={handleSearch} className="flex gap-2">
                <Input placeholder="Search users..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-48 bg-slate-800 border-slate-700" />
                <Button type="submit" size="sm" variant="outline"><Search className="h-4 w-4" /></Button>
              </form>
              <div className="flex items-center border border-slate-700 rounded-md overflow-hidden">
                <Button size="sm" variant={filters.humansOnly ? "default" : "ghost"} className={`rounded-none ${filters.humansOnly ? 'bg-emerald-600' : ''}`}
                  onClick={() => setFilters(f => ({ ...f, humansOnly: true, botsOnly: false }))}><Users className="h-4 w-4 mr-1" />Humans</Button>
                <Button size="sm" variant={filters.botsOnly ? "default" : "ghost"} className={`rounded-none ${filters.botsOnly ? 'bg-purple-600' : ''}`}
                  onClick={() => setFilters(f => ({ ...f, humansOnly: false, botsOnly: true }))}><Users className="h-4 w-4 mr-1" />Bots</Button>
                <Button size="sm" variant={!filters.humansOnly && !filters.botsOnly ? "default" : "ghost"} className={`rounded-none ${!filters.humansOnly && !filters.botsOnly ? 'bg-slate-600' : ''}`}
                  onClick={() => setFilters(f => ({ ...f, humansOnly: false, botsOnly: false }))}>All</Button>
              </div>
              <Button size="sm" variant={filters.proOnly ? "default" : "outline"} className={filters.proOnly ? "bg-yellow-600" : ""}
                onClick={() => setFilters(f => ({ ...f, proOnly: !f.proOnly }))}><Crown className="h-4 w-4 mr-1" />Pro</Button>
              <Button size="sm" variant={filters.bannedOnly ? "destructive" : "outline"}
                onClick={() => setFilters(f => ({ ...f, bannedOnly: !f.bannedOnly }))}><Ban className="h-4 w-4" /></Button>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500" onClick={() => setCreateModalOpen(true)} data-testid="add-user-btn">
                <UserPlus className="h-4 w-4 mr-1" /> Add User
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-3 px-4 text-slate-400 font-medium">User</th>
                  <th className="text-left py-3 px-4 text-slate-400 font-medium">Email</th>
                  <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
                  <th className="text-left py-3 px-4 text-slate-400 font-medium">Balance</th>
                  <th className="text-left py-3 px-4 text-slate-400 font-medium">Last Login</th>
                  <th className="text-right py-3 px-4 text-slate-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-slate-800 hover:bg-slate-800/50">
                    <td className="py-3 px-4"><span className="font-medium text-white">{user.username}</span></td>
                    <td className="py-3 px-4 text-slate-300">{user.email}</td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1 flex-wrap">
                        {user.is_bot && <Badge className="bg-purple-500/20 text-purple-400">Bot</Badge>}
                        {user.is_pro && <Badge className="bg-yellow-500/20 text-yellow-400">Pro</Badge>}
                        {user.is_pro && user.pro_no_charge && <Badge className="bg-emerald-500/20 text-emerald-400">Free</Badge>}
                        {user.is_banned && <Badge className="bg-red-500/20 text-red-400">Banned</Badge>}
                        {!user.is_pro && !user.is_banned && !user.is_bot && <Badge className="bg-slate-500/20 text-slate-400">Free Tier</Badge>}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-slate-300">V${(user.virtual_cash || user.virtual_balance_vd)?.toLocaleString() || '100,000'}</td>
                    <td className="py-3 px-4 text-slate-400 text-sm">{formatDate(user.last_login)}</td>
                    <td className="py-3 px-4">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => { setSelectedUser(user); fetchUserDetails(user.id); setViewModalOpen(true); }}><Eye className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => { setSelectedUser(user); setEditModalOpen(true); }}><Edit className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300" onClick={() => { setSelectedUser(user); setDeleteModalOpen(true); }}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-800">
            <span className="text-slate-400 text-sm">Showing {users.length} of {pagination.total} users</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={pagination.page === 1} onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}><ChevronLeft className="h-4 w-4" /></Button>
              <span className="px-3 py-1 text-slate-400">{pagination.page} / {pagination.pages}</span>
              <Button size="sm" variant="outline" disabled={pagination.page === pagination.pages} onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit User Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader><DialogTitle className="text-white">Edit User</DialogTitle><DialogDescription>Update user details for {selectedUser?.username}</DialogDescription></DialogHeader>
          {selectedUser && <EditUserForm user={selectedUser} onSave={(updates) => handleUpdateUser(selectedUser.id, updates)} onCancel={() => setEditModalOpen(false)} />}
        </DialogContent>
      </Dialog>

      {/* View User Modal */}
      <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl">
          <DialogHeader><DialogTitle className="text-white">User Details</DialogTitle></DialogHeader>
          {userDetails && <UserDetailsView details={userDetails} token={token} onRefresh={() => viewUserDetails(userDetails.user?.id)} />}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-red-400" /> Delete User</DialogTitle>
            <DialogDescription>Are you sure you want to delete {selectedUser?.username}? This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDeleteUser(selectedUser?.id)}>Delete User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><UserPlus className="h-5 w-5 text-emerald-400" /> Add New User</DialogTitle><DialogDescription>Create a new user account</DialogDescription></DialogHeader>
          <CreateUserForm onSave={handleCreateUser} onCancel={() => setCreateModalOpen(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
};

const EditUserForm = ({ user, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    username: user.username, email: user.email, is_pro: user.is_pro || false,
    pro_no_charge: user.pro_no_charge || false, is_banned: user.is_banned || false
  });
  return (
    <div className="space-y-4">
      <div><label className="text-sm text-slate-400">Username</label>
        <Input value={formData.username} onChange={(e) => setFormData(f => ({ ...f, username: e.target.value }))} className="bg-slate-800 border-slate-700" /></div>
      <div><label className="text-sm text-slate-400">Email</label>
        <Input value={formData.email} onChange={(e) => setFormData(f => ({ ...f, email: e.target.value }))} className="bg-slate-800 border-slate-700" /></div>
      <div className="space-y-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={formData.is_pro} onChange={(e) => setFormData(f => ({ ...f, is_pro: e.target.checked, pro_no_charge: e.target.checked ? f.pro_no_charge : false }))} className="rounded" />
          <Crown className="h-4 w-4 text-yellow-400" /><span className="text-slate-300">Pro Member</span>
        </label>
        {formData.is_pro && (
          <label className="flex items-center gap-2 cursor-pointer ml-6">
            <input type="checkbox" checked={formData.pro_no_charge} onChange={(e) => setFormData(f => ({ ...f, pro_no_charge: e.target.checked }))} className="rounded" />
            <DollarSign className="h-4 w-4 text-emerald-400" /><span className="text-slate-300">No Charge (Free Pro)</span>
          </label>
        )}
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={formData.is_banned} onChange={(e) => setFormData(f => ({ ...f, is_banned: e.target.checked }))} className="rounded" />
          <Ban className="h-4 w-4 text-red-400" /><span className="text-slate-300">Banned</span>
        </label>
      </div>
      <DialogFooter><Button variant="outline" onClick={onCancel}>Cancel</Button><Button onClick={() => onSave(formData)}>Save Changes</Button></DialogFooter>
    </div>
  );
};

const UserDetailsView = ({ details, token, onRefresh }) => {
  const { user, portfolio, recent_scores, payments } = details;
  const [resetting, setResetting] = useState(false);
  const [removingSymbol, setRemovingSymbol] = useState(null);

  const handleResetPortfolio = async () => {
    if (!window.confirm(`Reset ${user?.username}'s portfolio to V$100,000? All holdings will be cleared.`)) return;
    setResetting(true);
    try {
      const res = await fetch(`${API_URL}/api/portfolio/reset?target_user_id=${user?.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!res.ok) throw new Error('Failed');
      toast.success(`${user?.username}'s portfolio has been reset`);
      if (onRefresh) onRefresh();
    } catch {
      toast.error('Failed to reset portfolio');
    } finally {
      setResetting(false);
    }
  };

  const handleRemoveHolding = async (symbol) => {
    if (!window.confirm(`Remove ${symbol} from ${user?.username}'s portfolio and refund original cost?`)) return;
    setRemovingSymbol(symbol);
    try {
      const res = await fetch(`${API_URL}/api/portfolio/admin/remove-holding?target_user_id=${user?.id}&symbol=${symbol}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(`Removed ${symbol} — refunded V$${data.refund_amount?.toLocaleString()}`);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(`Failed to remove ${symbol}: ${err.message}`);
    } finally {
      setRemovingSymbol(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div><span className="text-slate-400 text-sm">Username</span><p className="text-white font-medium">{user?.username}</p></div>
        <div><span className="text-slate-400 text-sm">Email</span><p className="text-white">{user?.email}</p></div>
        <div><span className="text-slate-400 text-sm">Created</span><p className="text-white">{formatDate(user?.created_at)}</p></div>
        <div><span className="text-slate-400 text-sm">Last Login</span><p className="text-white">{formatDate(user?.last_login)}</p></div>
        <div><span className="text-slate-400 text-sm">Balance</span><p className="text-white">V${(user?.virtual_cash || user?.virtual_balance_vd)?.toLocaleString()}</p></div>
        <div><span className="text-slate-400 text-sm">Status</span>
          <div className="flex gap-1 mt-1">
            {user?.is_pro && <Badge className="bg-yellow-500/20 text-yellow-400">Pro</Badge>}
            {user?.is_banned && <Badge className="bg-red-500/20 text-red-400">Banned</Badge>}
            {!user?.is_pro && !user?.is_banned && <Badge className="bg-slate-500/20 text-slate-400">Free</Badge>}
          </div>
        </div>
      </div>
      {portfolio && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-white font-medium">Portfolio ({portfolio.holdings?.length || 0}/10)</h4>
            <Button
              onClick={handleResetPortfolio}
              disabled={resetting}
              variant="outline"
              size="sm"
              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              data-testid="admin-reset-portfolio-btn"
            >
              {resetting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5 mr-1" />}
              Reset All
            </Button>
          </div>
          {portfolio.holdings?.length > 0 ? (
            <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
              {portfolio.holdings.map((h) => {
                const value = (h.units || 0) * (h.current_price_vd || 0);
                const pct = h.buy_price_vd ? ((h.current_price_vd - h.buy_price_vd) / h.buy_price_vd * 100) : 0;
                const isSuspicious = Math.abs(pct) > 500 || h.buy_price_vd === 0 || value > 500000;
                return (
                  <div key={h.symbol} className={`flex items-center justify-between p-2 rounded-lg ${isSuspicious ? 'bg-red-900/20 border border-red-500/30' : 'bg-slate-800/50'}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-white font-medium text-sm">{h.symbol}</span>
                      {h.is_power_play && <Badge className="bg-amber-500/20 text-amber-400 text-[10px] px-1 py-0">3x</Badge>}
                      {isSuspicious && <Badge className="bg-red-500/20 text-red-400 text-[10px] px-1 py-0">!</Badge>}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <span className="text-white text-sm">V${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                        <span className={`text-xs ml-1 ${pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {pct.toFixed(1)}%
                        </span>
                      </div>
                      <Button
                        onClick={() => handleRemoveHolding(h.symbol)}
                        disabled={removingSymbol === h.symbol}
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-red-400 hover:bg-red-500/10"
                        data-testid={`remove-holding-${h.symbol}`}
                      >
                        {removingSymbol === h.symbol ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-slate-500 text-sm">No holdings</p>
          )}
        </div>
      )}
      {recent_scores?.length > 0 && (
        <div><h4 className="text-white font-medium mb-2">Recent Scores</h4>
          <div className="flex gap-2">
            {recent_scores.slice(0, 5).map((score, idx) => (
              <div key={idx} className="bg-slate-800/50 rounded-lg p-2 text-center">
                <p className="text-xs text-slate-400">Week {score.week}</p>
                <p className="text-white font-medium">{score.total_points} pts</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {payments?.length > 0 && (
        <div><h4 className="text-white font-medium mb-2">Payment History</h4>
          <div className="space-y-2">
            {payments.map((p, idx) => (
              <div key={idx} className="flex justify-between bg-slate-800/50 rounded-lg p-2">
                <span className="text-slate-300">{p.package_name}</span>
                <span className={p.payment_status === 'paid' ? 'text-emerald-400' : 'text-slate-400'}>${p.amount} - {p.payment_status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const CreateUserForm = ({ onSave, onCancel }) => {
  const [formData, setFormData] = useState({ username: '', email: '', password: '', is_pro: false, pro_no_charge: false });
  const [creating, setCreating] = useState(false);
  const handleSubmit = async () => {
    if (!formData.username || !formData.email || !formData.password) { toast.error('Please fill all required fields'); return; }
    setCreating(true); await onSave(formData); setCreating(false);
  };
  return (
    <div className="space-y-4">
      <div><label className="text-sm text-slate-400">Username *</label>
        <Input value={formData.username} onChange={(e) => setFormData(f => ({ ...f, username: e.target.value }))} className="bg-slate-800 border-slate-700" placeholder="johndoe" data-testid="create-user-username" /></div>
      <div><label className="text-sm text-slate-400">Email *</label>
        <Input type="email" value={formData.email} onChange={(e) => setFormData(f => ({ ...f, email: e.target.value }))} className="bg-slate-800 border-slate-700" placeholder="john@example.com" data-testid="create-user-email" /></div>
      <div><label className="text-sm text-slate-400">Password *</label>
        <Input type="password" value={formData.password} onChange={(e) => setFormData(f => ({ ...f, password: e.target.value }))} className="bg-slate-800 border-slate-700" placeholder="Enter password" data-testid="create-user-password" /></div>
      <div className="space-y-3 pt-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={formData.is_pro} onChange={(e) => setFormData(f => ({ ...f, is_pro: e.target.checked, pro_no_charge: e.target.checked ? f.pro_no_charge : false }))} className="rounded" />
          <Crown className="h-4 w-4 text-yellow-400" /><span className="text-slate-300">Pro Member</span>
        </label>
        {formData.is_pro && (
          <label className="flex items-center gap-2 cursor-pointer ml-6">
            <input type="checkbox" checked={formData.pro_no_charge} onChange={(e) => setFormData(f => ({ ...f, pro_no_charge: e.target.checked }))} className="rounded" />
            <DollarSign className="h-4 w-4 text-emerald-400" /><span className="text-slate-300">No Charge (Free Pro)</span>
          </label>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={creating} data-testid="create-user-submit">
          {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />} Create User
        </Button>
      </DialogFooter>
    </div>
  );
};
